import React, { useState, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { createRetailerRider, updateRetailerRider } from '../../actions/ridersAction.js';
import {
  TwoWheeler,
  DirectionsBike,
  DirectionsCar,
  AirportShuttle,
  LocalShipping,
  DirectionsWalk,
} from '@material-ui/icons';

// Carrier types must match the backend enum exactly:
// "Bicycle" | "Motorcycle" | "Car" | "Van" | "Truck" | "Walking"
const CARRIER_OPTIONS = [
  { value: 'Motorcycle', label: 'Motorcycle', Icon: TwoWheeler },
  { value: 'Bicycle',    label: 'Bicycle',    Icon: DirectionsBike },
  { value: 'Car',        label: 'Car',        Icon: DirectionsCar },
  { value: 'Van',        label: 'Van',        Icon: AirportShuttle },
  { value: 'Truck',      label: 'Truck',      Icon: LocalShipping },
  { value: 'Walking',    label: 'On foot',    Icon: DirectionsWalk },
];

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  mobile: '',
  email: '',
  password: '',
  carrierType: 'Motorcycle',
};

/* ─── Inline styles ─────────────────────────────────────────────────────── */
const S = {
  overlay: {
    backdropFilter: 'blur(4px)',
  },
  modalContent: {
    borderRadius: '16px',
    border: 'none',
    boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
    overflow: 'hidden',
  },
  header: {
    background: 'linear-gradient(135deg, #005370 0%, #0291a1 100%)',
    borderBottom: 'none',
    padding: '20px 24px 18px',
  },
  headerTitle: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '1.15rem',
    letterSpacing: '-0.01em',
    margin: 0,
  },
  headerClose: {
    filter: 'brightness(0) invert(1)',
    opacity: 0.85,
  },
  body: {
    padding: '28px 24px 8px',
    background: '#fff',
  },
  footer: {
    padding: '12px 24px 20px',
    background: '#fff',
    borderTop: '1px solid #f1f5f9',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
  },
  label: {
    fontWeight: 600,
    fontSize: '0.78rem',
    color: '#374151',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '5px',
  },
  input: {
    borderRadius: '8px',
    border: '1.5px solid #e2e8f0',
    padding: '9px 13px',
    fontSize: '0.9rem',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    outline: 'none',
  },
  group: {
    marginBottom: '16px',
  },
  carrierGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    marginTop: '4px',
  },
  carrierOption: (selected) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 13px',
    borderRadius: '10px',
    border: selected ? '2px solid #0291a1' : '1.5px solid #e2e8f0',
    background: selected ? '#e8f8fb' : '#fafafa',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: selected ? 600 : 400,
    color: selected ? '#005370' : '#374151',
    transition: 'all 0.18s ease',
    userSelect: 'none',
  }),
  btnDiscard: {
    padding: '9px 22px',
    borderRadius: '8px',
    border: '1.5px solid #e2e8f0',
    background: '#fff',
    color: '#6b7280',
    fontWeight: 600,
    fontSize: '0.88rem',
    cursor: 'pointer',
  },
  btnSubmit: {
    padding: '9px 28px',
    borderRadius: '8px',
    border: 'none',
    background: 'linear-gradient(135deg, #005370 0%, #0291a1 100%)',
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.88rem',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(2,145,161,0.28)',
    transition: 'opacity 0.2s',
  },
  sectionDivider: {
    borderTop: '1px solid #f1f5f9',
    margin: '18px 0 16px',
  },
  sectionLabel: {
    fontSize: '0.72rem',
    fontWeight: 700,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '10px',
  },
};

const RetailerRiderForm = ({ show, handleClose, vendorId, riderToEdit }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (riderToEdit) {
      setFormData({
        firstName: riderToEdit.firstName || '',
        lastName: riderToEdit.lastName || '',
        mobile: riderToEdit.mobile || '',
        email: riderToEdit.email || '',
        password: '',
        carrierType: riderToEdit.carrierType || 'Motorcycle',
      });
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [riderToEdit, show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (riderToEdit) {
        await dispatch(updateRetailerRider(formData, vendorId, riderToEdit._id));
      } else {
        await dispatch(createRetailerRider(formData, vendorId));
      }
    } finally {
      setSubmitting(false);
      handleClose();
    }
  };

  const inputProps = (name, type = 'text', extra = {}) => ({
    style: S.input,
    className: 'w-100',
    type,
    name,
    value: formData[name],
    onChange: handleChange,
    required: true,
    disabled: submitting,
    ...extra,
  });

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      centered
      contentClassName="border-0"
    >
      <div style={S.modalContent}>
        {/* Header */}
        <div style={S.header}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={S.headerTitle}>
              {riderToEdit ? 'Edit Rider' : 'Add New Rider'}
            </span>
            <button
              onClick={handleClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', ...S.headerClose }}
              aria-label="Close"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={S.body}>
          <Form onSubmit={handleSubmit} autoComplete="off">

            {/* Name row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={S.group}>
                <label style={S.label}>First Name</label>
                <input {...inputProps('firstName')} placeholder="e.g. John" />
              </div>
              <div style={S.group}>
                <label style={S.label}>Last Name</label>
                <input {...inputProps('lastName')} placeholder="e.g. Doe" />
              </div>
            </div>

            {/* Contact row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={S.group}>
                <label style={S.label}>Mobile</label>
                <input {...inputProps('mobile', 'tel')} placeholder="07xxxxxxxx" />
              </div>
              <div style={S.group}>
                <label style={S.label}>Email</label>
                <input {...inputProps('email', 'email', { autoComplete: 'off' })} placeholder="rider@email.com" />
              </div>
            </div>

            {/* Password — create only */}
            {!riderToEdit && (
              <div style={S.group}>
                <label style={S.label}>Password</label>
                <input {...inputProps('password', 'password', { autoComplete: 'new-password', placeholder: '••••••••••' })} />
              </div>
            )}

            {/* Carrier type picker */}
            <div style={S.sectionDivider} />
            <div style={S.sectionLabel}>Vehicle / Carrier Type</div>
            <div style={S.carrierGrid}>
              {CARRIER_OPTIONS.map(({ value, label, Icon }) => {
                const selected = formData.carrierType === value;
                return (
                  <div
                    key={value}
                    style={S.carrierOption(selected)}
                    onClick={() => !submitting && setFormData((prev) => ({ ...prev, carrierType: value }))}
                  >
                    <Icon style={{ fontSize: '1.1rem', color: selected ? '#0291a1' : '#9ca3af' }} />
                    {label}
                  </div>
                );
              })}
            </div>

            <div style={S.sectionDivider} />

            {/* Footer buttons inside form so submit works */}
            <div style={S.footer}>
              <button type="button" style={S.btnDiscard} onClick={handleClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" style={{ ...S.btnSubmit, opacity: submitting ? 0.7 : 1 }} disabled={submitting}>
                {submitting ? 'Saving…' : riderToEdit ? 'Update Rider' : 'Add Rider'}
              </button>
            </div>

          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default RetailerRiderForm;
